import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getShops, logActivity } from '../../services/adminMockData';
import { getAreasAdmin, setAreaActive, deleteArea } from '../../services/apiService';

export default function AdminAreasPage() {
  const navigate = useNavigate();
  // Areas live in the backend — the localStorage override store this page used
  // to read meant an area switched off here never reached the database, so the
  // consumer app kept showing it.
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [toast, setToast] = useState(null);
  const shops = useMemo(() => getShops(), []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setAreas(await getAreasAdmin());
    } catch (err) {
      setAreas([]);
      if (err?.status === 401) setLoadError('Je admin-sessie is verlopen. Log opnieuw in.');
      else if (err?.status === 403) setLoadError('Dit account heeft geen adminrechten voor gebieden.');
      else setLoadError(err?.message || 'Kon gebieden niet laden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener('walkin-admin-areas-change', refresh);
    return () => window.removeEventListener('walkin-admin-areas-change', refresh);
  }, [refresh]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const toggleActive = async (area) => {
    const next = !area.active;
    try {
      await setAreaActive(area.id, next);
    } catch (err) {
      showToast(err?.message || 'Wijzigen mislukt', 'error');
      return;
    }
    logActivity(next ? 'Gebied geactiveerd' : 'Gebied gedeactiveerd', { area: area.name });
    await refresh();
    showToast(
      next
        ? `✅ ${area.name} is weer actief`
        : `🚫 ${area.name} staat op inactief — niet meer zichtbaar in de app`,
      next ? 'success' : 'error'
    );
  };

  const shopCountByArea = useMemo(() => {
    const map = {};
    shops.forEach((s) => { map[s.area] = (map[s.area] || 0) + 1; });
    return map;
  }, [shops]);

  const handleDelete = async (area) => {
    const inUse = (area.retailerCount || 0) || shopCountByArea[area.name] || 0;
    if (inUse > 0) {
      alert(
        `Kan niet verwijderen — ${inUse} winkeliers actief in dit gebied. ` +
        `Zet het gebied op inactief als je het uit de app wilt halen.`
      );
      return;
    }
    if (!window.confirm(`Verwijder ${area.name} definitief?`)) return;
    try {
      await deleteArea(area.id);
    } catch (err) {
      showToast(err?.message || 'Verwijderen mislukt', 'error');
      return;
    }
    logActivity('Gebied verwijderd', { area: area.name });
    await refresh();
    showToast(`🗑️ ${area.name} verwijderd`, 'error');
  };

  return (
    <div>
      {toast && (
        <div className={`admin__toast admin__toast--${toast.type}`}>{toast.message}</div>
      )}
      <header className="admin-page-head">
        <div>
          <h1 className="admin-page-title">Gebieden</h1>
          <p className="admin-page-sub">
            {loading ? 'Gebieden laden…' : `${areas.length} WalkIn gebieden`}
          </p>
        </div>
        <button className="admin-btn admin-btn--orange" onClick={() => navigate('/admin/areas/new')}>
          + Nieuw gebied
        </button>
      </header>

      {loadError && (
        <div className="admin-card" style={{ textAlign: 'center', padding: '32px 20px' }}>
          <span style={{ fontSize: 48, display: 'block' }}>⚠️</span>
          <h3 style={{ margin: '10px 0 6px', color: '#991B1B' }}>Kon gebieden niet laden</h3>
          <p style={{ color: '#6B7280', margin: '0 0 16px' }}>{loadError}</p>
          <button className="admin-btn admin-btn--orange" onClick={refresh}>
            Opnieuw proberen
          </button>
        </div>
      )}

      <div className="admin-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
        {areas.map((a) => {
          const shopCount = shopCountByArea[a.name] || 0;
          return (
            <div className="admin-card" key={a.id} style={{ marginTop: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 800, color: '#1A3E5C', fontSize: 16 }}>
                    {a.name}
                  </div>
                  <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                    <span className="admin-mono" title={a.codeStored ? 'Gebiedscode' : 'Voorlopige code (nog niet opgeslagen)'}>{a.code}{a.codeStored ? '' : '*'}</span> · {a.city}{a.address ? ` · ${a.address}` : ''}
                  </div>
                </div>
                <span className={`admin-badge admin-badge--${a.status}`}>{a.status}</span>
              </div>

              <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                <div>
                  <div style={{ color: '#9CA3AF', fontWeight: 600 }}>GPS</div>
                  <div>
                    {Number(a.lat).toFixed(4)}, {Number(a.lng).toFixed(4)}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#9CA3AF', fontWeight: 600 }}>Radius</div>
                  <div>{Number(a.radius).toLocaleString('nl-NL')} m</div>
                </div>
                <div>
                  <div style={{ color: '#9CA3AF', fontWeight: 600 }}>Winkeliers</div>
                  <div><strong>{shopCount}</strong></div>
                </div>
                <div>
                  <div style={{ color: '#9CA3AF', fontWeight: 600 }}>Deals</div>
                  <div><strong>{a.dealCount}</strong></div>
                </div>
              </div>

              <div style={{ marginTop: 14, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  className="admin-btn admin-btn--small admin-btn--ghost"
                  onClick={() => navigate(`/admin/areas/${a.id}/edit`)}
                >
                  Bewerk
                </button>
                <a
                  className="admin-btn admin-btn--small admin-btn--ghost"
                  href={`https://www.google.com/maps/?q=${a.lat},${a.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  Bekijk op kaart
                </a>
                {a.active ? (
                  <button
                    className="admin-btn admin-btn--small admin-btn--danger"
                    onClick={() => toggleActive(a)}
                  >
                    Op inactief zetten
                  </button>
                ) : (
                  <button
                    className="admin-btn admin-btn--small"
                    style={{ background: '#16A34A' }}
                    onClick={() => toggleActive(a)}
                  >
                    Activeren
                  </button>
                )}
                <button
                  className="admin-btn admin-btn--small admin-btn--danger"
                  onClick={() => handleDelete(a)}
                >
                  Verwijder
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
